import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import API from '../api';
import { useUser } from '../context/UserContext';
import ChangePasswordModal from './ChangePasswordModal';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [passwordModal, setPasswordModal] = useState({ isOpen: false, userId: null, userName: '' });
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', last_name: '', email: '' });
  const navigate = useNavigate();
  const { isAdmin, canEdit, user: currentUser } = useUser();

  useEffect(() => {
    // Redirect non-admins
    if (!isAdmin()) {
      toast.error('Access denied: Admin privileges required');
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [isAdmin, navigate]);

  const fetchUsers = async () => {
    try {
      const response = await API.get('/users');
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await API.put(`/users/${userId}/role`, { role: newRole });
      toast.success('User role updated successfully');
      fetchUsers(); // Refresh the list
    } catch (err) {
      console.error('Error updating role:', err);
      toast.error(err.response?.data?.message || 'Failed to update user role');
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user ${userEmail}?`)) {
      return;
    }

    try {
      await API.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers(); // Refresh the list
    } catch (err) {
      console.error('Error deleting user:', err);
      toast.error(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleOpenPasswordModal = (userId, userName) => {
    setPasswordModal({ isOpen: true, userId, userName });
  };

  const handleClosePasswordModal = () => {
    setPasswordModal({ isOpen: false, userId: null, userName: '' });
  };

  const canChangePassword = (targetUser) => {
    // Admin can change any password except their own (via this UI)
    // Editor can change passwords of editors and viewers, but not admins
    if (targetUser.id === currentUser?.id) return false;

    if (currentUser?.role === 'admin') return true;
    if (currentUser?.role === 'editor' && targetUser.role !== 'admin') return true;

    return false;
  };

  const startEditing = (user) => {
    if (user.id === currentUser?.id) return; // Cannot edit own account
    setEditingUser(user.id);
    setEditForm({
      name: user.name || '',
      last_name: user.last_name || '',
      email: user.email || ''
    });
  };

  const cancelEditing = () => {
    setEditingUser(null);
    setEditForm({ name: '', last_name: '', email: '' });
  };

  const handleSaveUser = async (userId) => {
    try {
      await API.put(`/users/${userId}`, editForm);
      toast.success('User information updated successfully');
      setEditingUser(null);
      fetchUsers(); // Refresh the list
    } catch (err) {
      console.error('Error updating user:', err);
      toast.error(err.response?.data?.message || 'Failed to update user information');
    }
  };

  const canEditUser = (targetUser) => {
    // Cannot edit own account
    if (targetUser.id === currentUser?.id) return false;
    // Only admin and editor can edit
    return canEdit();
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'editor':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'viewer':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">User Management</h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
          >
            Back to Dashboard
          </button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className={user.id === currentUser?.id ? 'bg-blue-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingUser === user.id ? (
                      <div className="flex flex-col space-y-1">
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          placeholder="First name"
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="text"
                          value={editForm.last_name}
                          onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                          placeholder="Last name"
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    ) : (
                      <div
                        className={`text-sm font-medium text-gray-900 ${canEditUser(user) ? 'cursor-pointer hover:text-blue-600' : ''}`}
                        onClick={() => canEditUser(user) && startEditing(user)}
                        title={canEditUser(user) ? 'Click to edit' : ''}
                      >
                        {user.name} {user.last_name}
                        {user.id === currentUser?.id && (
                          <span className="ml-2 text-xs text-blue-600">(You)</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingUser === user.id ? (
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        placeholder="email@example.com"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <div
                        className={`text-sm text-gray-500 ${canEditUser(user) ? 'cursor-pointer hover:text-blue-600' : ''}`}
                        onClick={() => canEditUser(user) && startEditing(user)}
                        title={canEditUser(user) ? 'Click to edit' : ''}
                      >
                        {user.email}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getRoleBadgeColor(
                        user.role
                      )}`}
                    >
                      {user.role.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                    {editingUser === user.id ? (
                      <>
                        <button
                          onClick={() => handleSaveUser(user.id)}
                          className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition"
                        >
                          Save
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        {user.id !== currentUser?.id && (
                          <>
                            {isAdmin() && (
                              <select
                                value={user.role}
                                onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="admin">Admin</option>
                                <option value="editor">Editor</option>
                                <option value="viewer">Viewer</option>
                              </select>
                            )}
                            {canChangePassword(user) && (
                              <button
                                onClick={() => handleOpenPasswordModal(user.id, `${user.name} ${user.last_name}`)}
                                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                              >
                                Change Password
                              </button>
                            )}
                            {isAdmin() && (
                              <button
                                onClick={() => handleDeleteUser(user.id, user.email)}
                                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition"
                              >
                                Delete
                              </button>
                            )}
                          </>
                        )}
                        {user.id === currentUser?.id && (
                          <span className="text-gray-400 text-xs italic">Cannot modify own account</span>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">Role Permissions:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>
              <strong>Admin:</strong> Full access including user management and can change any user's password
            </li>
            <li>
              <strong>Editor:</strong> Can create, update, delete customers, and change passwords for editors and viewers
            </li>
            <li>
              <strong>Viewer:</strong> Read-only access to customers and history
            </li>
          </ul>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={passwordModal.isOpen}
        onClose={handleClosePasswordModal}
        userId={passwordModal.userId}
        userName={passwordModal.userName}
        isOwnPassword={false}
      />
    </div>
  );
}
